import { useState, useEffect } from 'react';
import { DialogContent, DialogActions } from '@mui/material';
import { ThemeButton } from '@shared/ui/buttons';
import CardDisplay from '@widgets/cards/CardDisplay';
import { useForm, useWatch } from 'react-hook-form';
import { BasicTextarea, AmountInput } from '@shared/ui/forms/inputs';
import { useTranslation } from 'react-i18next';
import { useCards } from '@app/api/cards/useCards';
import {
  validateServerErrors,
  validateMaxLength,
  validateTransactionLimit,
} from '@utils/validation';
import { getDirtyValues } from '@utils/helpers';
import { useSelector } from 'react-redux';
import { formatBalanceUi, FRACTION_DIGITS } from '@utils/balance-helpers';
import { cardStatuses } from '@utils/statuses';

export default function EditCardDialog({ close, data }) {
  const [isLoading, setIsLoading] = useState(false);
  const { editCard } = useCards();
  const { tariff } = useSelector((state) => state.tariff);
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields, errors },
    setError,
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      transaction_limit: data?.transaction_limit,
      monthly_balance_limit: data?.limit,
      note: data?.note || '',
    },
  });

  const isBlocked = cardStatuses[data?.status] === 'blocked';

  async function onSubmit(formData) {
    const payload = {
      card_id: data?.id,
      ...getDirtyValues(dirtyFields, formData),
    };
    setIsLoading(true);
    try {
      const res = await editCard(payload);
      if (res) close();
    } catch (error) {
      validateServerErrors(setError, error);
    } finally {
      setIsLoading(false);
    }
  }

  const transactionLimitWatch = useWatch({
    control,
    name: 'transaction_limit',
  });
  const monthlyBalanceLimitWatch = useWatch({
    control,
    name: 'monthly_balance_limit',
  });

  useEffect(() => {
    if (transactionLimitWatch != null && monthlyBalanceLimitWatch != null) {
      trigger('transaction_limit');
    }
  }, [monthlyBalanceLimitWatch, transactionLimitWatch]);

  return (
    <>
      <DialogContent className="no-pd">
        <div className="form">
          <div className="customDialog-card-px">
            <CardDisplay data={data} />
          </div>
          <div className="form block-pd-x">
            <div className="formGroup-grid">
              <AmountInput
                integer
                isRequired={true}
                control={control}
                name="transaction_limit"
                label={t('transaction_limit')}
                placeholder={`${t('max')} ${formatBalanceUi(tariff?.transaction_limit, null, FRACTION_DIGITS.INTEGER)}`}
                max={tariff?.transaction_limit}
                currency={data?.currency || 'USD'}
                hint={`${t('max')} ${formatBalanceUi(tariff?.transaction_limit, data?.currency, FRACTION_DIGITS.INTEGER)}`}
                error={errors.transaction_limit}
                validation={(value) =>
                  validateTransactionLimit(value, monthlyBalanceLimitWatch)
                }
                disabled={isBlocked}
              />
              <AmountInput
                integer
                isRequired={true}
                control={control}
                name="monthly_balance_limit"
                label={t('balance_limit')}
                placeholder={`${t('max')} ${formatBalanceUi(tariff?.monthly_card_spent_limit, null, FRACTION_DIGITS.INTEGER)}`}
                max={tariff?.monthly_card_spent_limit}
                currency={data?.currency || 'USD'}
                hint={`${t('max')} ${formatBalanceUi(tariff?.monthly_card_spent_limit, data?.currency, FRACTION_DIGITS.INTEGER)}`}
                error={errors.monthly_balance_limit}
                disabled={isBlocked}
              />
            </div>
            <BasicTextarea
              control={control}
              name="note"
              label={t('Note')}
              placeholder={t('Note')}
              counter={60}
              isRequired={false}
              validation={validateMaxLength}
              error={errors.note}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <ThemeButton
          minWidth
          startIcon="check"
          block={false}
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={!isDirty}
        >
          {t('save')}
        </ThemeButton>
      </DialogActions>
    </>
  );
}
